import { GetUserResponseBody } from "@/api/dto/users"
import { capitalized } from "@/utils/string"

type Translate = (key: string, options?: Record<string, unknown>) => string

const ACCOUNT_DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
	year: "numeric",
	month: "long",
	day: "numeric",
}

const ACCOUNT_ACTIVITY_EVENT_TYPE_KEYS: Record<string, string> = {
	account_creation: "account.activityTypeAccountCreation",
	pay_as_you_go_purchase: "account.activityTypePayAsYouGoPurchase",
	referral_bonus: "account.activityTypeReferralBonus",
	welcome_bonus: "account.activityTypeWelcomeBonus",
	promotional_bonus: "account.activityTypePromotionalBonus",
	membership: "account.activityTypeMembership",
	usage: "account.activityTypeUsage",
	admin_action: "account.activityTypeAdminAction",
	credit_expiration: "account.activityTypeCreditExpiration",
}

const LEGACY_SUBSCRIPTION_TIER_KEYS: Record<string, string> = {
	essential: "account.subscriptionTierEssential",
	founders: "account.subscriptionTierFounders",
	premium: "account.subscriptionTierPremium",
	standard: "account.subscriptionTierStandard",
	ultimate: "account.subscriptionTierUltimate",
	unlimited: "account.subscriptionTierUnlimited",
	pro: "account.proPlan",
	free: "account.none",
	none: "account.none",
}

const LEGACY_SUBSCRIPTION_TERM_KEYS: Record<string, string> = {
	monthly: "account.billingTermMonthly",
	yearly: "account.billingTermYearly",
	annual: "account.billingTermYearly",
	one_time: "account.billingTermOneTime",
}

const ACRONYM_TOKENS: Record<string, string> = {
	ai: "AI",
	api: "API",
	id: "ID",
	payg: "PAYG",
}

const normalizeIdentifier = (value: string): string =>
	value
		.trim()
		.toLowerCase()
		.replace(/[\s-]+/g, "_")

const toValidDate = (value: Date | string | null | undefined): Date | undefined => {
	if (!value) {
		return undefined
	}
	const date = value instanceof Date ? value : new Date(value)
	return Number.isNaN(date.getTime()) ? undefined : date
}

const formatWithLocale = (date: Date, language: string): string => {
	try {
		return new Intl.DateTimeFormat(language, ACCOUNT_DATE_FORMAT_OPTIONS).format(date)
	} catch {
		return new Intl.DateTimeFormat("en", ACCOUNT_DATE_FORMAT_OPTIONS).format(date)
	}
}

export const formatAccountDate = (
	value: Date | string | null | undefined,
	language: string,
): string | undefined => {
	const date = toValidDate(value)
	if (!date) {
		return undefined
	}

	return formatWithLocale(date, language)
}

export const getGenAiResetDate = (
	user: GetUserResponseBody | undefined,
	language: string,
): string | undefined => {
	const subscriptionPools = (user?.genAiCreditPools ?? []).filter(
		(pool) => pool.grantType === "subscription" && pool.expiresAt,
	)

	if (subscriptionPools.length === 0) {
		return undefined
	}

	const soonest = subscriptionPools.reduce<Date | null>((currentSoonest, pool) => {
		const date = toValidDate(pool.expiresAt)
		if (!date) {
			return currentSoonest
		}
		if (!currentSoonest || date < currentSoonest) {
			return date
		}
		return currentSoonest
	}, null)

	return soonest ? formatWithLocale(soonest, language) : undefined
}

export const getLegacySubscriptionTierDisplay = (tier: string, t: Translate): string => {
	const normalizedTier = normalizeIdentifier(tier)
	if (!normalizedTier) {
		return t("account.none")
	}

	const tierKey = LEGACY_SUBSCRIPTION_TIER_KEYS[normalizedTier]
	if (tierKey) {
		return t(tierKey)
	}

	return humanizeIdentifier(tier)
}

export const getLegacySubscriptionTermDisplay = (term: string, t: Translate): string => {
	const normalizedTerm = normalizeIdentifier(term)
	if (!normalizedTerm) {
		return t("account.none")
	}

	const termKey = LEGACY_SUBSCRIPTION_TERM_KEYS[normalizedTerm]
	if (termKey) {
		return t(termKey)
	}

	return humanizeIdentifier(term)
}

const humanizeIdentifier = (value: string): string =>
	normalizeIdentifier(value)
		.split("_")
		.filter(Boolean)
		.map((word) => ACRONYM_TOKENS[word] ?? capitalized(word))
		.join(" ")

export const getActivityEventTypeDisplay = (eventType: string, t: Translate): string => {
	const normalizedEventType = normalizeIdentifier(eventType)
	const key = ACCOUNT_ACTIVITY_EVENT_TYPE_KEYS[normalizedEventType]
	if (key) {
		const translated = t(key)
		if (translated !== key) {
			return translated
		}
	}

	return humanizeIdentifier(eventType)
}
